import type { IncomingHttpHeaders } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import type { Locale } from '@packages/config';
import config from '@packages/config';
import { TrackingContext } from '@packages/tracking';
import { useUserAgentDevice } from '@packages/shared';
import type { SearchParametersInput } from '@packages/gql/generated/shopping/graphql';
import type { Device } from '@packages/themes/src/default';
import { useCookies } from '@packages/utilities';

import {
  decodeCategoryFilter,
  decodeOrder,
  decodePage,
  decodePromotionId,
  decodeSelectedFilterValues,
  decodeUserInteraction,
} from '../queryEncoding';
import { getChannelFromDevice } from '../hooks/useChannel';
import {
  PERSONALIZATION_COOKIE_NAME,
  parsePersonalizationString,
} from '../hooks/usePersonalizationCookie';

const prepareHighlightArticles = (commaSeparatedArticleNumbers: string) =>
  commaSeparatedArticleNumbers
    .split(',')
    .filter((x) => x)
    .map((articleNumber) => ({ articleNumber }));

const channelMapping = {
  web: 'DESKTOP',
  tab: 'TABLET',
  mob: 'MOBILE',
} as const;

const buildSharedGqlVariables = (
  query: ParsedUrlQuery,
  locale: Locale,
  device: Device,
  isBot: boolean,
  cookies: Record<string, string>,
  referrer?: string,
) => {
  const {
    host: { domain },
  } = config;

  const personalizationString = cookies[PERSONALIZATION_COOKIE_NAME];

  const { segment: personalizationSegment } = parsePersonalizationString(personalizationString);

  const visitorId = cookies.SESSIONID;

  const userInteraction = decodeUserInteraction(query);
  const categoryId = decodeCategoryFilter(query);
  const promotionId = decodePromotionId(query);
  const highlightArticles =
    typeof query.highlight === 'string' ? prepareHighlightArticles(query.highlight) : [];

  const simplifiedReferrer = referrer?.includes(domain) ? undefined : 'extern';

  const { currentPage, pageSize: limit } = decodePage(query);
  const offset = currentPage && currentPage >= 1 ? (currentPage - 1) * limit : 0;

  const sortBy = decodeOrder(query) || 'relevance';

  return {
    params: {
      environment: {
        locale,
        channel: channelMapping[getChannelFromDevice(device)],
        isBot,
      },
      version: '22',
      constraints: {
        appliedFilters: Object.entries(decodeSelectedFilterValues(query)).map(
          ([filterId, values]) => ({
            filterId,
            values,
          }),
        ),
        ...(categoryId && { categoryId }),
      },
      ...(highlightArticles.length && {
        overrides: { highlightArticles },
      }),
      tracking: {
        ...(personalizationSegment && { personalizationSegment }),
        ...(visitorId && { visitorId }),
        ...(userInteraction && { userInteraction }),
        ...(promotionId && { promotionId }),
        ...(referrer && { referrer: simplifiedReferrer }),
      },
      paging: {
        limit,
        offset,
        sortBy,
      },
    } satisfies SearchParametersInput,
  };
};

export const getSharedServerSideGqlVariables = (
  query: ParsedUrlQuery,
  locale: Locale,
  headers: IncomingHttpHeaders,
  cookies: Record<string, string>,
  referrer: string | undefined,
) => {
  const device = headers['x-ua-device'] as Device;
  const isBot = headers['x-ua-bot'] !== undefined;

  return buildSharedGqlVariables(query, locale as Locale, device, isBot, cookies, referrer);
};

export const useSharedClientSideGqlVariables = (referrer: string | undefined) => {
  const { query } = useRouter();
  const { locale } = useIntl();

  const { isBot } = useContext(TrackingContext) ?? { isBot: false };
  const device = useUserAgentDevice();
  const { getCookies } = useCookies();
  const cookies = getCookies();

  return buildSharedGqlVariables(query, locale as Locale, device, isBot, cookies, referrer);
};
